import { createSlice } from "@reduxjs/toolkit";
import { AccessLegacyState } from "../types";
import { fetchLegacyTransactionDetailsThunk } from "../thunk/legacyThunk";

/* ---------------- INITIAL STATE ---------------- */

const initialState: AccessLegacyState = {
  /* -------- MAP + COORDINATES -------- */
  start_latitude: 0,
  start_longitude: 0,
  end_latitude: 0,
  end_longitude: 0,

  start_chainage_km: "",
  start_chainage_m: "",
  end_chainage_km: "",
  end_chainage_m: "",
  start_chainage: "",
  end_chainage: "",

  /* -------- STEPPER -------- */
  activeStep: 0,
  coordinateSite: "start",

  /* -------- FORM / LEGACY -------- */
  taluka: "",
  district: "",
  khasra_number: "",
  national_highway_number: "",
  pincode: "",
  state: "",
  village: "",
  address_1: "",
  address_2: "",
  highway_side: "",
  amenityType: "",
  amenity: "",
  utility_type: "",
  category_id: "",
  access_id: null,
  licenseExpiry: "",
  location_type_name: "",
  location_type_id: "",
  access_category_id: "",
  file_name: "",
  highway_category: "",
  property_type_id: "",
  application_category_id: 0,
  access_category_name: "",
  authority: "",
  status_id: 0,
  utilized_nh_land_area: "",
  license_type: "",
  license_type_selection: "",
  proposed_utility_alignment: "",
  payment_transaction_date: "",
  payment_amount_paid: "",
  payment_transaction_id: "",
  payment_receipt_name: "",
  payment_receipt_object_key: "",
  payment_receipt_file_name: "",
  legacy_substage_id: "",
  legacy_substage_name: "",
  legacy_id: null,

  /* -------- TRANSACTION DETAILS -------- */
  transactionDetails: [],
  transactionDetailsLoading: false,

  /* -------- FILE -------- */
  uploadedFile: null,

  /* -------- META -------- */
  legacyData: null,
  along_chainage_details: [],
  crossing_details: [],
};

/* ---------------- SLICE ---------------- */

const legacyAmenitySlice = createSlice({
  name: "legacyAmenity",
  initialState,

  reducers: {
    /* -------- GENERIC FIELD UPDATE -------- */
    setLegacyField(state, action) {
      const { name, value } = action.payload;
      (state as any)[name] = value;
    },

    /* -------- BULK UPDATE -------- */
    setLegacyFields(state, action) {
      Object.assign(state, action.payload);
    },

    setPropertyTypeId(state, action) {
      state.property_type_id = action.payload;
    },

    /* -------- STEPPER -------- */
    setLegacyActiveStep(state, action) {
      state.activeStep = action.payload;
    },

    setLegacyCoordinateSite(state, action) {
      state.coordinateSite = action.payload;
    },

    /* -------- MAP CLICK HANDLER -------- */
    setLegacyCoordinates(state, action) {
      const { site, latitude, longitude, km, m } = action.payload;

      (state as any)[`${site}_latitude`] = latitude;
      (state as any)[`${site}_longitude`] = longitude;
      (state as any)[`${site}_chainage_km`] = km;
      (state as any)[`${site}_chainage_m`] = m;
    },

    /* -------- FILE -------- */
    setLegacyUploadedFile(state, action) {
      state.uploadedFile = action.payload;
    },

    /* -------- LEGACY DATA -------- */
    setLegacyData(state, action) {
      state.legacyData = action.payload;
    },

    /* -------- RESET -------- */
    resetLegacyAmenityState() {
      return initialState;
    },
    setActiveStep(state, action) {
      state.activeStep = action.payload;
    },
    setAlongChainages(state, action) {
      state.along_chainage_details = action.payload;
    },

    setAcrossChainages(state, action) {
      state.crossing_details = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchLegacyTransactionDetailsThunk.pending, (state) => {
        state.transactionDetailsLoading = true;
      })
      .addCase(
        fetchLegacyTransactionDetailsThunk.fulfilled,
        (state, action: any) => {
          state.transactionDetailsLoading = false;
          const payload = action.payload?.data;
          if (!payload) {
            state.transactionDetails = [];
          } else {
            // API returns a single object; normalize to array
            state.transactionDetails = Array.isArray(payload)
              ? payload
              : [payload];
          }
        },
      )
      .addCase(fetchLegacyTransactionDetailsThunk.rejected, (state) => {
        state.transactionDetailsLoading = false;
        state.transactionDetails = [];
      });
  },
});

/* -------- EXPORTS (Before Extra Reducers) -------- */

export const {
  setLegacyField,
  setLegacyFields,
  setLegacyActiveStep,
  setLegacyCoordinateSite,
  setLegacyCoordinates,
  setLegacyUploadedFile,
  setLegacyData,
  resetLegacyAmenityState,
  setPropertyTypeId,
  setActiveStep,
  setAcrossChainages,
  setAlongChainages,
} = legacyAmenitySlice.actions;

export default legacyAmenitySlice.reducer;
